export type PurchaseProductId =
  | "hint_pack_small"
  | "life_refill"
  | "streak_shield"
  | "starter_bundle"
  | "theme_candy_pop"
  | "theme_forest_glow"
  | "ad_free_pass";

export type PurchasePlatform = "web-mock" | "revenuecat-native";

export interface PurchaseGrant {
  hints?: number;
  nexus?: number;
  streakShields?: number;
  unlimitedLives?: number;
  perkId?: string;
  activeTheme?: string;
}

export interface PurchaseProduct {
  id: PurchaseProductId;
  storeProductId: string;
  title: string;
  description: string;
  priceLabel: string;
  type: "consumable" | "nonConsumable";
  grant: PurchaseGrant;
}

export interface PurchaseResult {
  ok: boolean;
  product?: PurchaseProduct;
  transactionId?: string;
  platform: PurchasePlatform;
  message: string;
}

export interface RestoreResult {
  products: PurchaseProduct[];
  platform: PurchasePlatform;
  message: string;
}

export const PRODUCT_CATALOG: Record<PurchaseProductId, PurchaseProduct> = {
  hint_pack_small: {
    id: "hint_pack_small",
    storeProductId: "com.lexilink.hints.small",
    title: "Hint Pack",
    description: "Adds 5 reveals plus a little Nexus for tough days.",
    priceLabel: "$0.99",
    type: "consumable",
    grant: { hints: 5, nexus: 20 }
  },
  life_refill: {
    id: "life_refill",
    storeProductId: "com.lexilink.lives.refill",
    title: "3 Lives",
    description: "Adds 3 Unlimited lives immediately.",
    priceLabel: "$0.99",
    type: "consumable",
    grant: { unlimitedLives: 3 }
  },
  streak_shield: {
    id: "streak_shield",
    storeProductId: "com.lexilink.streak_shield",
    title: "Streak Shield",
    description: "Protects one missed daily streak when you come back and win.",
    priceLabel: "$0.99",
    type: "consumable",
    grant: { streakShields: 1 }
  },
  starter_bundle: {
    id: "starter_bundle",
    storeProductId: "com.lexilink.bundle.starter",
    title: "Starter Bundle",
    description: "10 hints, 100 Nexus, and one Streak Shield.",
    priceLabel: "$1.99",
    type: "consumable",
    grant: { hints: 10, nexus: 100, streakShields: 1 }
  },
  theme_candy_pop: {
    id: "theme_candy_pop",
    storeProductId: "com.lexilink.theme.candy_pop",
    title: "Candy Pop",
    description: "Bright pinks, mint greens, and playful tile glow.",
    priceLabel: "$0.99",
    type: "nonConsumable",
    grant: { perkId: "theme-candy", activeTheme: "theme-candy" }
  },
  theme_forest_glow: {
    id: "theme_forest_glow",
    storeProductId: "com.lexilink.theme.forest_glow",
    title: "Forest Glow",
    description: "A cozy green-gold theme for quieter play.",
    priceLabel: "$0.99",
    type: "nonConsumable",
    grant: { perkId: "theme-forest", activeTheme: "theme-forest" }
  },
  ad_free_pass: {
    id: "ad_free_pass",
    storeProductId: "com.lexilink.ad_free",
    title: "Ad-Free Pass",
    description: "Removes interstitial ad placements from the app.",
    priceLabel: "$1.49",
    type: "nonConsumable",
    grant: { perkId: "ad-free" }
  }
};

export const STORE_PRODUCTS = Object.values(PRODUCT_CATALOG);

export interface PurchaseAdapter {
  platform: PurchasePlatform;
  available: boolean;
  purchase(productId: PurchaseProductId): Promise<PurchaseResult>;
  restore(): Promise<RestoreResult>;
}

function isNativeRuntime() {
  return typeof window !== "undefined" && window.location.protocol === "capacitor:";
}

export function getPurchaseAdapter(): PurchaseAdapter {
  if (isNativeRuntime()) {
    return nativePendingAdapter;
  }

  return webMockAdapter;
}

const webMockAdapter: PurchaseAdapter = {
  platform: "web-mock",
  available: true,
  async purchase(productId) {
    const product = PRODUCT_CATALOG[productId];
    return {
      ok: true,
      product,
      transactionId: `mock-${productId}-${Date.now()}`,
      platform: "web-mock",
      message: "Test purchase complete"
    };
  },
  async restore() {
    return {
      products: [],
      platform: "web-mock",
      message: "No test purchases to restore"
    };
  }
};

const nativePendingAdapter: PurchaseAdapter = {
  platform: "revenuecat-native",
  available: true,
  async purchase(productId) {
    try {
      const { Purchases, LOG_LEVEL, PRODUCT_CATEGORY } = await import("@revenuecat/purchases-capacitor");
      await configureRevenueCat(Purchases, LOG_LEVEL);

      const catalogProduct = PRODUCT_CATALOG[productId];
      const { products } = await Purchases.getProducts({
        productIdentifiers: [catalogProduct.storeProductId],
        type: PRODUCT_CATEGORY.NON_SUBSCRIPTION
      });
      const storeProduct = products.find((product) => product.identifier === catalogProduct.storeProductId);

      if (!storeProduct) {
        return {
          ok: false,
          platform: "revenuecat-native",
          message: `${catalogProduct.storeProductId} is not available in the store yet`
        };
      }

      const result = await Purchases.purchaseStoreProduct({ product: storeProduct });
      return {
        ok: true,
        product: catalogProduct,
        transactionId: result.transaction?.transactionIdentifier ?? `${result.productIdentifier}-${Date.now()}`,
        platform: "revenuecat-native",
        message: "Purchase complete"
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : "Purchase could not be completed";
      return {
        ok: false,
        platform: "revenuecat-native",
        message: message.toLowerCase().includes("cancel") ? "Purchase canceled" : message
      };
    }
  },
  async restore() {
    try {
      const { Purchases, LOG_LEVEL } = await import("@revenuecat/purchases-capacitor");
      await configureRevenueCat(Purchases, LOG_LEVEL);
      const { customerInfo } = await Purchases.restorePurchases();
      const purchasedIds = new Set(customerInfo.allPurchasedProductIdentifiers ?? []);
      const restoredProducts = STORE_PRODUCTS.filter(
        (product) => product.type === "nonConsumable" && purchasedIds.has(product.storeProductId)
      );

      return {
        products: restoredProducts,
        platform: "revenuecat-native",
        message: restoredProducts.length ? "Purchases restored" : "No purchases to restore"
      };
    } catch (error) {
      return {
        products: [],
        platform: "revenuecat-native",
        message: error instanceof Error ? error.message : "Restore failed"
      };
    }
  }
};

let revenueCatConfigured: Promise<void> | undefined;

async function configureRevenueCat(
  Purchases: typeof import("@revenuecat/purchases-capacitor").Purchases,
  LOG_LEVEL: typeof import("@revenuecat/purchases-capacitor").LOG_LEVEL
) {
  revenueCatConfigured ??= (async () => {
    const { Capacitor } = await import("@capacitor/core");
    const platform = Capacitor.getPlatform();
    const apiKey =
      platform === "ios"
        ? import.meta.env.VITE_REVENUECAT_IOS_API_KEY
        : platform === "android"
          ? import.meta.env.VITE_REVENUECAT_ANDROID_API_KEY
          : import.meta.env.VITE_REVENUECAT_TEST_API_KEY;

    if (!apiKey) {
      throw new Error(`Missing RevenueCat ${platform} API key`);
    }

    if (import.meta.env.DEV) {
      await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
    }

    await Purchases.configure({
      apiKey,
      appUserID: import.meta.env.VITE_REVENUECAT_APP_USER_ID || null
    });
  })();

  return revenueCatConfigured;
}
